import { element as getElement, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import easyfyToaster, { MessageSeverity } from '@shared/easyfy.toaster';
import { hide, show } from '@shared/easyfy.utils';
import basketService from '@webServices/basket.service';
import { AddToCartState } from '@shared/easyfy.enums';

namespace Easyfy.Modules {
  @invoke
  export class Basket {
    constructor() {
      new easyfyToaster(null);
    }

    onInit() {

      globalEvent({
        type: 'click',
        event: {
          action: 'add-to-cart',
          callback: Basket.onAddToCart
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'basketrow-change',
          callback: Basket.onBasketRowChange
        }
      });

      globalEvent({
        type: 'change',
        event: {
          action: 'basketrow-set',
          callback: Basket.onBasketRowSet
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'basketrow-remove',
          callback: Basket.onBasketRowRemove
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'decrease-quantity',
          callback: Basket.onDecreaseQuantity
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'increase-quantity',
          callback: Basket.onIncreaseQuantity
        }
      });
    }

    static onAddToCart = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLElement = e.target as HTMLElement;
      let quantity: number = 1;

      /* Change element state to 'processing'. */
      element.dataset["state"] = AddToCartState.Processing;
      element.setAttribute("disabled", "disabled");
      if (element.querySelectorAll(".state").length === 0) {
        element.appendChild(document.createElement("div")).classList.add("state");
      }

      if (element.dataset.quantityInput) {
        let quantityInput = document.querySelector(element.dataset.quantityInput) as HTMLInputElement;

        if (quantityInput) {
          quantity = parseInt(quantityInput.value);
        }
      }

      await basketService.UpdateBasket({
        rows: [
          {
            productId: element.attributes['data-productid']?.value,
            changeQty: quantity,
            trackingData: {
              actionOrigin: element.attributes['data-action-origin']?.value,
              searchProductOrigin: element.attributes['data-searchproduct-id']?.value
            }
          }
        ]
      }, element.attributes['data-token']?.value).then((response) => {
        Basket.renderBasket(response);
        if (response.data.message) {
          /*Default is error/fail.*/
          let messageSeverity: MessageSeverity = MessageSeverity.error;
          let elementState = AddToCartState.Fail;

          if (response.data.success === true) {
            messageSeverity = MessageSeverity.success;
            elementState = AddToCartState.Success;
          }
          easyfyToaster.toaster(messageSeverity, response.data.message);
          element.dataset["state"] = elementState;
        }
      }).catch(error => {
        easyfyToaster.toaster(MessageSeverity.error, "Error");

        /* Change element state to 'Fail'. */
        element.dataset["state"] = AddToCartState.Fail;
      });
      element.removeAttribute("disabled");

      /* Remove element state. */
      setTimeout(() => {
        element.removeAttribute("data-state");
        element.querySelector(".state").remove();
      }, 3000);
      return false;
    }

    static onBasketRowChange = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      let element: HTMLElement = e.target as HTMLElement;

      if (!element.attributes['data-rowid']) {
        element = e.target.parentElement;
      }

      const rowId = element.attributes['data-rowid']?.value;
      const quantity = element.attributes['data-add'] ? 1 : -1;

      await basketService.UpdateBasket({
        rows: [
          {
            rowId: rowId,
            changeQty: quantity,
            trackingData: {
              actionOrigin: element.attributes['data-action-origin']?.value,
              searchProductOrigin: element.attributes['data-searchproduct-id']?.value
            }
          }
        ]
      }, element.attributes['data-token']?.value).then((response) => {
        Basket.renderBasket(response);
        if (response.data.message)
          easyfyToaster.toaster((response.status == 200 && response.data.success) ? MessageSeverity.success : MessageSeverity.error, response.data.message, null, null, true);
      }).catch(error => {
        easyfyToaster.toaster(MessageSeverity.error, "Error", null, null, true);
      });

      return false;
    }

    static onBasketRowSet = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLInputElement = e.target as HTMLInputElement;
      const rowId = element.attributes['data-rowid']?.value;
      const quantity: number = parseInt(element.value);

      await basketService.UpdateBasket({
        rows: [
          {
            rowId: rowId,
            setQty: quantity,
            trackingData: {
              actionOrigin: element.attributes['data-action-origin']?.value,
              searchProductOrigin: element.attributes['data-searchproduct-id']?.value
            }
          }

        ]
      }, element.attributes['data-token']?.value).then((response) => {
        Basket.renderBasket(response);
        if (response.data.message)
          easyfyToaster.toaster((response.status == 200 && response.data.success) ? MessageSeverity.success : MessageSeverity.error, response.data.message, null, null, true);
      }).catch(error => {
        easyfyToaster.toaster(MessageSeverity.error, "Error", null, null, true);
      });

      return false;
    }

    static onBasketRowRemove = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLElement = e.target as HTMLElement;

      await basketService.UpdateBasket({
        rows: [
          {
            rowId: element.attributes['data-rowid']?.value,
            setQty: 0,
            trackingData: {
              actionOrigin: element.attributes['data-action-origin']?.value,
              searchProductOrigin: element.attributes['data-searchproduct-id']?.value
            }
          }
        ]
      }, element.attributes['data-token']?.value).then((response) => {
        Basket.renderBasket(response);
        if (response.data.message)
          easyfyToaster.toaster((response.status == 200 && response.data.success) ? MessageSeverity.success : MessageSeverity.error, response.data.message, null, null, true);
      }).catch(error => {
        easyfyToaster.toaster(MessageSeverity.error, "Error", null, null, true);
      });

      return false;
    }

    static onDecreaseQuantity() {
      let quantityInput = document.getElementById('quantity') as HTMLInputElement;

      if (quantityInput) {
        quantityInput.stepDown();
      }

      return false;
    }

    static onIncreaseQuantity() {
      let quantityInput = document.getElementById('quantity') as HTMLInputElement;

      if (quantityInput) {
        quantityInput.stepUp();
      }

      return false;
    }

    static getBasket = async () => {
      await basketService.GetBasket().then((response) => {
        Basket.renderBasket(response);
      }).catch(error => {

      });

      return false;
    }

    static renderBasket = (response: any) => {
      if (response.status == 200 && response.data != null) {
        const headerBasket: HTMLElement = getElement('[data-container="header-basket"]') as HTMLElement;
        const miniBasket: HTMLElement = getElement('[data-container="mini-basket"]') as HTMLElement;
        const priceElement: HTMLElement = headerBasket.querySelector('[data-container="basket-price"]') as HTMLElement;
        const quantityElement: HTMLElement = headerBasket.querySelector('[data-container="basket-quantity"]') as HTMLElement;
        const headerCheckout: HTMLElement = document.querySelector('[data-container="header-checkout"]') as HTMLElement;

        if (response.data.totalQuantity > 0) {
          if (priceElement) {
            priceElement.innerHTML = response.data.totalPrice;
            //show(priceElement);
          }
          
          if (quantityElement) {
            quantityElement.innerHTML = response.data.totalQuantity;  
            show(quantityElement, "flex");
          }

          if (headerBasket) {
            headerBasket.removeAttribute("disabled");
          }

          if (headerCheckout) {
            headerCheckout.removeAttribute("disabled");
          }
          
        } else {
          if (quantityElement) {
            hide(quantityElement);
          }

          if (priceElement) {
            //hide(priceElement);
            priceElement.innerHTML = priceElement.dataset.basketText;
          }

          if (headerBasket) {
            headerBasket.setAttribute("disabled", "disabled");
          }

          if (headerCheckout) {
            headerCheckout.setAttribute("disabled", "disabled");
          }
        }
        
        if (miniBasket) {
          miniBasket.innerHTML = response.data.partial;
        }
      }

      return false;
    }
  }
}

export default Easyfy.Modules;